import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { utilStyles, KeyboardCode } from '@paypalcorp/pp-react-utils';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import { CheckmarkIcon } from '@paypalcorp/pp-react-icons';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {
      listItem: function listItem() {},
      align: function () {}
    };
  }

  var sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceXs = tokens.sysSpaceXs,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover,
      sysColorBackgroundSurfaceActive = tokens.sysColorBackgroundSurfaceActive,
      sysBorderThicknessFocusOutline = tokens.sysBorderThicknessFocusOutline,
      sysBorderRadiusSm = tokens.sysBorderRadiusSm;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles;

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: list_base;\n    list-style: none;\n    padding: 0;\n    margin: 0;\n    background-color: transparent;\n  "])));
  var listItemWrapper = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: list_item_wrapper;\n    padding: ", " ", ";\n    display: flex;\n    text-decoration: none;\n    background-color: ", ";\n    outline: none;\n\n    > div:not(:last-child),\n    a:not(:last-child) {\n      margin-inline-end: ", ";\n    }\n  "])), sysSpaceMd, sysSpaceLg, sysColorBackgroundSurface, sysSpaceSm);
  var hoverAndActiveStyles = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: hover_active;\n    cursor: pointer;\n    position: relative;\n    &:hover {\n      background-color: ", ";\n    }\n    &:active {\n      background-color: ", ";\n    }\n  "])), sysColorBackgroundSurfaceHover, sysColorBackgroundSurfaceActive);
  var highlight = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: highlight;\n    background-color: ", ";\n  "])), sysColorBackgroundSurfaceHover);
  var decoration = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: decoration;\n    align-self: center;\n    display: inherit;\n    color: ", "; // Possible Breaking Change - Review later with team.\n    pointer-events: none;\n    flex-grow: 0;\n  "])), sysColorPrimaryMain);
  var content = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: content;\n    align-self: center;\n    display: inline-flex;\n    flex-direction: column;\n    gap: ", ";\n    pointer-events: none;\n    flex-grow: 1;\n  "])), sysSpaceXs);
  var action = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: action;\n    * {\n      pointer-events: auto;\n    }\n  "])));
  var trailingContent = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: trailing_content;\n    align-self: center;\n    text-align: right;\n    display: flex;\n    flex-direction: column;\n    gap: ", ";\n    pointer-events: none;\n    flex-grow: 0;\n    [dir='rtl'] & {\n      text-align: left;\n      justify-self: end;\n    }\n  "])), sysSpaceXs);
  var text = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: text;\n    margin: 0;\n  "])));
  var labelDescription = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: secondary_text;\n    ", ";\n    color: ", ";\n  "])), text, sysColorBackgroundMediumContrast);

  var align = function () {
    var alignment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'top';
    return css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n      align-self: ", " || 'start';\n    "])), {
      top: 'start',
      bottom: 'end',
      center: 'center'
    }[alignment]);
  };

  var focusActive = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: keyboard_active;\n    position: relative;\n    &:focus {\n      &:after {\n        ", "\n        z-index: 1; // to keep focus indicator on top of hover\n      }\n    }\n  "])), focusStyles.offsetFocusAfter({
    horizontalOffset: sysBorderThicknessFocusOutline,
    radius: sysBorderRadiusSm,
    verticalOffset: '0rem'
  }));
  return {
    base: base,
    listItemWrapper: listItemWrapper,
    hoverAndActiveStyles: hoverAndActiveStyles,
    highlight: highlight,
    content: content,
    action: action,
    decoration: decoration,
    trailingContent: trailingContent,
    text: text,
    labelDescription: labelDescription,
    align: align,
    focusActive: focusActive
  };
}

var _excluded = ["label", "labelLineClamp", "status", "labelContext", "labelDescription", "labelDescriptionLineClamp", "alert", "action", "value", "decoration", "trailingContent", "navigationOptions", "className", "focusActive", "isFocused", "isHighlighted", "isSelected", "as", "isItemFocusable", "isItemNavigational", "role"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint-disable complexity */

var ListItem = function (_ref) {
  var label = _ref.label,
      labelLineClamp = _ref.labelLineClamp,
      status = _ref.status,
      labelContext = _ref.labelContext,
      labelDescription = _ref.labelDescription,
      labelDescriptionLineClamp = _ref.labelDescriptionLineClamp,
      alert = _ref.alert,
      action = _ref.action,
      value = _ref.value,
      decoration = _ref.decoration,
      trailingContent = _ref.trailingContent,
      navigationOptions = _ref.navigationOptions,
      className = _ref.className,
      focusActive = _ref.focusActive,
      isFocused = _ref.isFocused,
      isHighlighted = _ref.isHighlighted,
      isSelected = _ref.isSelected,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'div' : _ref$as,
      isItemFocusable = _ref.isItemFocusable,
      isItemNavigational = _ref.isItemNavigational,
      role = _ref.role,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var listRef = useRef();
  useEffect(function () {
    if (isFocused) {
      listRef.current.focus();
    }
  }, [isFocused]);
  var listItemWrapperCSS = [_styles.listItemWrapper, isItemNavigational && _styles.hoverAndActiveStyles, isHighlighted && _styles.highlight, isItemFocusable && _styles.focusActive];
  var decorationCSS = [_styles.decoration, decoration && _styles.align(decoration.align)];
  var trailingContentCSS = [_styles.trailingContent, trailingContent && _styles.align(trailingContent.align)];

  var navigationProps = navigationOptions && _objectSpread({}, navigationOptions);

  return jsx(as, _extends({
    css: listItemWrapperCSS,
    role: role,
    tabIndex: -1,
    ref: listRef
  }, navigationProps, rest, {
    "data-ppui": "true"
  }), (decoration === null || decoration === void 0 ? void 0 : decoration.thumbnail) && jsx("div", {
    css: decorationCSS,
    "data-ppui": "true"
  }, decoration.thumbnail), (!!label || !!alert || !!action || !!status || !!labelContext || !!labelDescription) && jsx("div", {
    css: _styles.content,
    "data-ppui": "true"
  }, status && jsx("div", {
    "data-ppui": "true"
  }, status), labelContext && jsx(CaptionText, {
    as: "p",
    css: _styles.labelDescription,
    "data-ppui": "true"
  }, labelContext), label && jsx(BodyText, {
    as: "p",
    strong: isSelected,
    css: _styles.text,
    clampAtLine: labelLineClamp,
    "data-ppui": "true"
  }, label), labelDescription && jsx(CaptionText, {
    as: "p",
    css: _styles.labelDescription,
    clampAtLine: labelDescriptionLineClamp,
    "data-ppui": "true"
  }, labelDescription), alert, action && jsx("div", {
    css: _styles.action,
    "data-ppui": "true"
  }, action)), (trailingContent || isSelected) && jsx("div", {
    css: trailingContentCSS,
    "data-ppui": "true"
  }, isSelected ? jsx(CheckmarkIcon, {
    size: "sm",
    color: "SelectedMain",
    "data-ppui": "true"
  }) : null, (trailingContent === null || trailingContent === void 0 ? void 0 : trailingContent.status) && jsx("div", {
    "data-ppui": "true"
  }, trailingContent.status), (trailingContent === null || trailingContent === void 0 ? void 0 : trailingContent.labelContext) && jsx(CaptionText, {
    as: "p",
    css: _styles.labelDescription,
    "data-ppui": "true"
  }, trailingContent.labelContext), (trailingContent === null || trailingContent === void 0 ? void 0 : trailingContent.label) && jsx(BodyText, {
    as: "p",
    css: _styles.text,
    "data-ppui": "true"
  }, trailingContent.label), (trailingContent === null || trailingContent === void 0 ? void 0 : trailingContent.labelDescription) && jsx(CaptionText, {
    as: "p",
    css: _styles.labelDescription,
    "data-ppui": "true"
  }, trailingContent.labelDescription), (trailingContent === null || trailingContent === void 0 ? void 0 : trailingContent.action) && jsx("div", {
    css: _styles.action,
    "data-ppui": "true"
  }, trailingContent.action)));
};

var _excluded$1 = ["options", "listStyles", "focusActive", "activeIndex", "focusedIndex", "onKeyDown", "onClick", "onFocus", "onBlur", "itemAs"];
var List = React.forwardRef(function (_ref, ref) {
  var options = _ref.options,
      listStyles = _ref.listStyles,
      focusActive = _ref.focusActive,
      _ref$activeIndex = _ref.activeIndex,
      activeIndex = _ref$activeIndex === void 0 ? -1 : _ref$activeIndex,
      _ref$focusedIndex = _ref.focusedIndex,
      focusedIndex = _ref$focusedIndex === void 0 ? -1 : _ref$focusedIndex,
      onKeyDown = _ref.onKeyDown,
      onClick = _ref.onClick,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      _ref$itemAs = _ref.itemAs,
      itemAs = _ref$itemAs === void 0 ? ListItem : _ref$itemAs,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var listRef = useRef();
  var computedRef = ref || listRef;

  var _useState = useState(focusedIndex),
      _useState2 = _slicedToArray(_useState, 2),
      focusedIndexRef = _useState2[0],
      setFocusedIndex = _useState2[1];

  useEffect(function () {
    setFocusedIndex(focusedIndex);
  }, [focusedIndex]);

  var handleClick = function (evt, index) {
    setFocusedIndex(index);
    /* istanbul ignore next */

    if (onClick) {
      onClick(evt);
    }
  };

  return jsx("menu", _extends({
    css: [_styles.base, listStyles],
    ref: computedRef,
    tabIndex: focusActive && focusedIndexRef === -1 ? 0 : -1
  }, rest, {
    onFocus: function handleListFocus(evt) {
      var isFocusedCurrent = focusActive && evt.target === computedRef.current && evt.currentTarget === computedRef.current;

      if (!onKeyDown && isFocusedCurrent) {
        setFocusedIndex(0);
      }

      if (onFocus) {
        onFocus(evt);
      }
    },
    onBlur: function handleListBlur(evt) {
      var isTargetContained = !computedRef.current.contains(evt.target) || !evt.relatedTarget;

      if (!onKeyDown && focusActive && isTargetContained) {
        setFocusedIndex(-1);
      }

      if (onBlur) {
        onBlur(evt);
      }
    },
    onKeyDown: function handleKey(evt) {
      if (onKeyDown) {
        onKeyDown(evt);
      } else {
        /* istanbul ignore next */
        var KeyCode = new KeyboardCode(evt.key);

        if (KeyCode.IS_ARROW_UP && focusedIndexRef > 0) {
          evt.preventDefault();
          setFocusedIndex(focusedIndexRef - 1);
        } else if (KeyCode.IS_ARROW_DOWN && focusedIndexRef < options.length - 1) {
          evt.preventDefault();
          setFocusedIndex(focusedIndexRef + 1);
        } else if ((KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) && onClick) {
          evt.preventDefault();
          onClick(evt);
        }
      }
    },
    "data-ppui": "true"
  }), function listItems(optList, ItemAs) {
    return optList.map(function (option, index) {
      var _option$value;

      if (typeof option.label !== 'string' && !option.value) {
        console.warn('When label is a React Element, `value` property is necessary in option');
      }

      var hasOnClick = onClick || option.onClick;
      var isItemNavigational = !!hasOnClick || !!option.navigationOptions;
      var otherProps = {
        value: (_option$value = option.value) !== null && _option$value !== void 0 ? _option$value : option.label,
        role: hasOnClick ? 'button' : null,
        focusActive: focusActive,
        isFocused: focusActive && focusedIndexRef === index,
        isHighlighted: activeIndex === index,
        isItemNavigational: isItemNavigational,
        isItemFocusable: focusActive !== null && focusActive !== void 0 ? focusActive : isItemNavigational,
        as: option.navigationOptions ? 'a' : 'div'
      };
      return jsx("li", {
        className: option.className //@ts-ignore
        ,
        onClick: function (evt) {
          return handleClick(evt, index);
        },
        key: index,
        "aria-selected": option.isSelected,
        "data-ppui": "true"
      }, jsx(ItemAs, _extends({}, otherProps, option, {
        "data-ppui": "true"
      })));
    });
  }(options, itemAs));
});

export { List, ListItem, styles as listStyles };
